import type {
  RoiCumulativePoint,
  RoiDataset,
  RoiFailureMode,
  RoiFirefightMetricBucket,
  RoiIntegrationCoverage,
  RoiIntegrationStep,
  RoiLateMetricBucket,
  RoiProject,
  RoiReworkBucket,
  RoiSignal,
  RoiSignalSource,
  RoiVerdict,
} from '../types/roi';

const ENGINEERING_HEADCOUNT = 800;
const BLENDED_SALARY_USD = 200_000;
const WORKING_DAYS_PER_YEAR = 260;
const RATE_PER_ENGINEER_DAY_USD = Math.round(
  BLENDED_SALARY_USD / WORKING_DAYS_PER_YEAR
);
const RATE_PER_ENGINEER_HOUR_USD = Math.round(RATE_PER_ENGINEER_DAY_USD / 8);

const RECOVERABLE_REWORK_HOURS_PER_PERSON_PER_MONTH = 2.5;
const MONTHS_PER_YEAR = 12;

const REWORK_RECOVERY_USD = Math.round(
  RECOVERABLE_REWORK_HOURS_PER_PERSON_PER_MONTH *
    ENGINEERING_HEADCOUNT *
    RATE_PER_ENGINEER_HOUR_USD *
    MONTHS_PER_YEAR
);

function failureModesFor(
  reworkEngineerDays: number,
  weeksSlipped: number,
  firefightEngineerDays: number
): RoiFailureMode[] {
  const modes: RoiFailureMode[] = [];
  if (reworkEngineerDays > 0) modes.push('rework');
  if (weeksSlipped > 0) modes.push('late');
  if (firefightEngineerDays > 0) modes.push('firefight');
  return modes;
}

const heroProjects: RoiProject[] = [
  (() => {
    const reworkEngineerDays = 90;
    const weeksSlipped = 4;
    const firefightEngineerDays = 22;
    const signals: RoiSignal[] = [
      {
        source: 'jira',
        ref: 'PAY-9281',
        description:
          'Same Jira ticket reopened 3 times within 12 days with major scope edits - strong rework signature.',
        dayOffset: 14,
      },
      {
        source: 'github',
        ref: 'PR #18204',
        description:
          'Revert commit on the idempotency-key migration two days after merge.',
        dayOffset: 22,
      },
      {
        source: 'slack',
        ref: '#payments-eng - "rolling back webhook dedupe"',
        description:
          'Thread between four engineers debating rollback strategy - Intera flags this as rework-in-progress.',
        dayOffset: 24,
      },
      {
        source: 'jira',
        ref: 'PAY-9412, PAY-9418, PAY-9433',
        description:
          'Three follow-up tickets opened against the same epic after the planned ship date.',
        dayOffset: 71,
      },
      {
        source: 'pagerduty',
        ref: 'INC-2041',
        description:
          'P1 incident 9 days post-launch - duplicate-charge bug Intera would have linked to the unresolved rework signature.',
        dayOffset: 88,
      },
    ];
    return {
      id: 'payments-idempotency-hardening',
      name: 'Payments idempotency hardening',
      team: 'Payments Platform',
      tier: 'hero',
      verdict: 'caught',
      verdictRationale:
        "Three reopen events on the same Jira ticket inside two weeks - Intera's rework detector would have surfaced this on day 14.",
      failureModes: failureModesFor(
        reworkEngineerDays,
        weeksSlipped,
        firefightEngineerDays
      ),
      reworkEngineerDays,
      weeksSlipped,
      firefightEngineerDays,
      plannedShip: 'Q2 2025',
      actualShip: 'Q3 2025 (+4 wk)',
      headcount: 9,
      signals,
      narrative:
        'The Payments Platform team set out to make webhook delivery exactly-once by introducing an idempotency-key layer in front of the charge endpoints. The design assumed a single source of truth for keys, but partway through implementation it became clear the legacy refunds path used a different keying scheme. The team rewrote the dedupe layer twice and shipped four weeks late; nine days after launch a P1 incident exposed a duplicate-charge edge case that consumed a week of incident response.',
    };
  })(),

  (() => {
    const reworkEngineerDays = 42;
    const weeksSlipped = 2;
    const firefightEngineerDays = 18;
    const signals: RoiSignal[] = [
      {
        source: 'github',
        ref: 'PR #19042',
        description:
          'PR went through 11 review rounds - Intera flags abnormally long review cycles on a single change.',
        dayOffset: 18,
      },
      {
        source: 'confluence',
        ref: 'Search Re-ranking PRD',
        description:
          'PRD updated 3 times after engineering started - Intera detects scope drift between plan and ticket.',
        dayOffset: 21,
      },
      {
        source: 'jira',
        ref: 'SRCH-1402',
        description:
          'Ticket reopened with major description rewrite - Intera classifies this as silent re-scoping.',
        dayOffset: 35,
      },
      {
        source: 'github',
        ref: 'PR #19388',
        description:
          'Hotfix PR merged 6 days after release, restoring the v1 ranker for fallback.',
        dayOffset: 76,
      },
    ];
    return {
      id: 'search-reranking-v2',
      name: 'Search re-ranking v2',
      team: 'Search',
      tier: 'hero',
      verdict: 'caught',
      verdictRationale:
        '11 review rounds on the central PR plus a PRD revised three times mid-build - Intera would have surfaced the scope drift before the release.',
      failureModes: failureModesFor(
        reworkEngineerDays,
        weeksSlipped,
        firefightEngineerDays
      ),
      reworkEngineerDays,
      weeksSlipped,
      firefightEngineerDays,
      plannedShip: 'Q3 2025',
      actualShip: 'Q3 2025 (+2 wk)',
      headcount: 6,
      signals,
      narrative:
        'Search shipped a new learning-to-rank model after three quarters of work. The PRD was edited mid-implementation to widen the scope from product search to global search, but the ticket and the model evaluation harness were never updated. The new ranker degraded queries in two non-product surfaces, requiring a partial rollback and a follow-up re-tune.',
    };
  })(),

  (() => {
    const reworkEngineerDays = 84;
    const weeksSlipped = 6;
    const firefightEngineerDays = 31;
    const signals: RoiSignal[] = [
      {
        source: 'jira',
        ref: 'MOB-3318',
        description:
          'Three duplicate bug reports opened against background-sync in the week after the iOS 18 beta.',
        dayOffset: 9,
      },
      {
        source: 'slack',
        ref: '#ios-foundations - "sync drop investigation"',
        description:
          'Long thread debugging the same symptom across two builds - Intera flags repeated investigations as rework.',
        dayOffset: 12,
      },
      {
        source: 'github',
        ref: 'PR #21044 (reverted)',
        description:
          'The first overhaul attempt is reverted entirely two weeks after merge.',
        dayOffset: 26,
      },
      {
        source: 'jira',
        ref: 'MOB-3402..MOB-3411',
        description:
          'Ten follow-up tickets opened against the same epic, all after the planned ship date.',
        dayOffset: 88,
      },
      {
        source: 'pagerduty',
        ref: 'INC-2188',
        description:
          'Push-notification delivery regression traced back to the sync changes - three days of firefighting.',
        dayOffset: 121,
      },
    ];
    return {
      id: 'ios-background-sync-overhaul',
      name: 'iOS background sync overhaul',
      team: 'Mobile Foundations',
      tier: 'hero',
      verdict: 'caught',
      verdictRationale:
        "A full revert PR plus a cluster of duplicate bug reports two weeks after iOS 18 beta - Intera's revert + duplicate-bug detectors would both have fired.",
      failureModes: failureModesFor(
        reworkEngineerDays,
        weeksSlipped,
        firefightEngineerDays
      ),
      reworkEngineerDays,
      weeksSlipped,
      firefightEngineerDays,
      plannedShip: 'Q2 2025',
      actualShip: 'Q4 2025 (+6 wk)',
      headcount: 7,
      signals,
      narrative:
        'The Mobile Foundations team rewrote the iOS background sync engine to support the new BGTaskScheduler API and to fix a long-standing battery regression. The first cut shipped to TestFlight and immediately produced duplicate-message drops; the team reverted the entire commit, rebuilt the diff against the OS 18 betas, and shipped six weeks late. Push delivery regressed for three days afterward.',
    };
  })(),

  (() => {
    const reworkEngineerDays = 45;
    const weeksSlipped = 5;
    const firefightEngineerDays = 14;
    const signals: RoiSignal[] = [
      {
        source: 'jira',
        ref: 'RISK-771',
        description:
          'Same ticket reopened 4 times - repeated cycle of "ready for review" → reopened with new requirements.',
        dayOffset: 17,
      },
      {
        source: 'confluence',
        ref: 'Risk Model Training Runbook',
        description:
          'Runbook page edited 8 times in the build window - Intera classifies as procedure-drift.',
        dayOffset: 33,
      },
      {
        source: 'github',
        ref: 'PR #17891 (force-pushed 6 times)',
        description:
          'Force-push count on the main migration PR is in the 99th percentile across the eng org.',
        dayOffset: 42,
      },
      {
        source: 'slack',
        ref: '#risk-platform - "model parity diff"',
        description:
          'Repeated parity-check debates between the old and new pipelines - Intera flags as unresolved rework.',
        dayOffset: 51,
      },
    ];
    return {
      id: 'risk-model-retraining-pipeline',
      name: 'Risk model retraining pipeline',
      team: 'Risk Platform',
      tier: 'hero',
      verdict: 'caught',
      verdictRationale:
        "Four reopens of the central ticket and a force-pushed PR in the 99th percentile - Intera's rework signature would have fired by week three.",
      failureModes: failureModesFor(
        reworkEngineerDays,
        weeksSlipped,
        firefightEngineerDays
      ),
      reworkEngineerDays,
      weeksSlipped,
      firefightEngineerDays,
      plannedShip: 'Q3 2025',
      actualShip: 'Q4 2025 (+5 wk)',
      headcount: 5,
      signals,
      narrative:
        'Risk Platform migrated the offline retraining pipeline from a bespoke scheduler to Airflow. Parity checks between the old and new pipelines kept failing on the same three feature columns, and the team rebuilt the feature-engineering step twice before reaching parity. The migration shipped five weeks late.',
    };
  })(),

  (() => {
    const reworkEngineerDays = 64;
    const weeksSlipped = 3;
    const firefightEngineerDays = 26;
    const signals: RoiSignal[] = [
      {
        source: 'jira',
        ref: 'INFRA-2204 (reopened ×3)',
        description:
          "Reopened three times in three weeks - Intera's rework detector would have fired on the second reopen.",
        dayOffset: 19,
      },
      {
        source: 'github',
        ref: 'PR #22118 → PR #22240 (revert)',
        description: 'Migration PR reverted six days after merge.',
        dayOffset: 41,
      },
      {
        source: 'pagerduty',
        ref: 'INC-2299, INC-2301, INC-2304',
        description:
          'Three connector-related incidents within 14 days of the second attempt.',
        dayOffset: 68,
      },
      {
        source: 'slack',
        ref: '#kafka-migration - "consumer-lag investigation"',
        description:
          'Long-running debugging thread spanning two on-call rotations.',
        dayOffset: 73,
      },
    ];
    return {
      id: 'kafka-consumer-migration',
      name: 'Kafka consumer migration to KIP-848',
      team: 'Core Infrastructure',
      tier: 'hero',
      verdict: 'caught',
      verdictRationale:
        "A reverted migration PR followed by three incidents within two weeks of the second attempt - Intera's post-launch firefight detector would have fired immediately.",
      failureModes: failureModesFor(
        reworkEngineerDays,
        weeksSlipped,
        firefightEngineerDays
      ),
      reworkEngineerDays,
      weeksSlipped,
      firefightEngineerDays,
      plannedShip: 'Q1 2025',
      actualShip: 'Q2 2025 (+3 wk)',
      headcount: 8,
      signals,
      narrative:
        'Core Infrastructure migrated all internal Kafka consumers to the new group-protocol from KIP-848. The first migration attempt was reverted after six days due to consumer lag on the largest topics; the second attempt shipped successfully but generated three connector-related pages over the following fortnight.',
    };
  })(),
];

interface StandardTemplate {
  name: string;
  team: string;
  plannedShip: string;
  actualShip: string;
  headcount: number;
  reworkDays: number;
  lateWeeks: number;
  firefightDays: number;
}

const caughtTemplates: StandardTemplate[] = [
  {
    name: 'OAuth token rotation',
    team: 'Identity & Access',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 14,
    lateWeeks: 3,
    firefightDays: 6,
  },
  {
    name: 'Redis topology shift',
    team: 'Storage',
    plannedShip: 'Q2 2025',
    actualShip: 'Q2 2025 (+1 wk)',
    headcount: 5,
    reworkDays: 9,
    lateWeeks: 1,
    firefightDays: 12,
  },
  {
    name: 'Postgres sharding strategy',
    team: 'Storage',
    plannedShip: 'Q1 2025',
    actualShip: 'Q3 2025 (+8 wk)',
    headcount: 6,
    reworkDays: 22,
    lateWeeks: 8,
    firefightDays: 4,
  },
  {
    name: 'Event bus refactor',
    team: 'Core Infrastructure',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+4 wk)',
    headcount: 5,
    reworkDays: 17,
    lateWeeks: 4,
    firefightDays: 9,
  },
  {
    name: 'ElasticSearch upgrade to 8.x',
    team: 'Search',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+5 wk)',
    headcount: 4,
    reworkDays: 12,
    lateWeeks: 5,
    firefightDays: 7,
  },
  {
    name: 'Rate limiting v3',
    team: 'API Gateway',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 8,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'GraphQL subgraph integration',
    team: 'API Gateway',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+9 wk)',
    headcount: 7,
    reworkDays: 26,
    lateWeeks: 9,
    firefightDays: 11,
  },
  {
    name: 'Session state migration',
    team: 'Identity & Access',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 5,
  },
  {
    name: 'Web checkout A/B framework',
    team: 'Checkout',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 5,
    reworkDays: 10,
    lateWeeks: 2,
    firefightDays: 8,
  },
  {
    name: 'Notification delivery v2',
    team: 'Notifications',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+4 wk)',
    headcount: 6,
    reworkDays: 15,
    lateWeeks: 4,
    firefightDays: 14,
  },
  {
    name: 'Account merge tooling',
    team: 'Trust & Safety',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 3,
    reworkDays: 13,
    lateWeeks: 3,
    firefightDays: 6,
  },
  {
    name: 'Webhook retry queue',
    team: 'Payments Platform',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 4,
    reworkDays: 9,
    lateWeeks: 2,
    firefightDays: 7,
  },
  {
    name: 'Pricing engine v3',
    team: 'Monetization',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+6 wk)',
    headcount: 8,
    reworkDays: 24,
    lateWeeks: 6,
    firefightDays: 11,
  },
  {
    name: 'Permissions matrix refactor',
    team: 'Identity & Access',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+4 wk)',
    headcount: 5,
    reworkDays: 16,
    lateWeeks: 4,
    firefightDays: 5,
  },
  {
    name: 'Observability metrics rollup',
    team: 'Observability',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 9,
  },
  {
    name: 'Android in-app browser overhaul',
    team: 'Mobile Core',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+4 wk)',
    headcount: 5,
    reworkDays: 14,
    lateWeeks: 4,
    firefightDays: 10,
  },
  {
    name: 'Feature flag service hardening',
    team: 'Web Platform',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 7,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'CI pipeline parallelization',
    team: 'Developer Productivity',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+1 wk)',
    headcount: 4,
    reworkDays: 6,
    lateWeeks: 1,
    firefightDays: 3,
  },
  {
    name: 'ML feature store v2',
    team: 'ML Infra',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+10 wk)',
    headcount: 7,
    reworkDays: 28,
    lateWeeks: 10,
    firefightDays: 8,
  },
  {
    name: 'Push notification routing',
    team: 'Notifications',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 8,
    lateWeeks: 2,
    firefightDays: 5,
  },
  {
    name: 'iOS payment sheet redesign',
    team: 'Mobile Foundations',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 10,
    lateWeeks: 3,
    firefightDays: 6,
  },
  {
    name: 'CRM bulk-import endpoint',
    team: 'Sales Engineering',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+5 wk)',
    headcount: 4,
    reworkDays: 17,
    lateWeeks: 5,
    firefightDays: 4,
  },
  {
    name: 'Data warehouse cost optimization',
    team: 'Data Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+3 wk)',
    headcount: 5,
    reworkDays: 13,
    lateWeeks: 3,
    firefightDays: 6,
  },
  {
    name: 'Vendor SDK sandbox',
    team: 'Partner Integrations',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+4 wk)',
    headcount: 3,
    reworkDays: 11,
    lateWeeks: 4,
    firefightDays: 3,
  },
  {
    name: 'Background job retry policy',
    team: 'Core Infrastructure',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 4,
    reworkDays: 9,
    lateWeeks: 2,
    firefightDays: 7,
  },
  {
    name: 'Web app code-splitting',
    team: 'Web Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+1 wk)',
    headcount: 3,
    reworkDays: 6,
    lateWeeks: 1,
    firefightDays: 4,
  },
  {
    name: 'Analytics event schema v3',
    team: 'Data Platform',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 5,
  },
  {
    name: 'Customer export pipeline',
    team: 'Trust & Safety',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+4 wk)',
    headcount: 3,
    reworkDays: 12,
    lateWeeks: 4,
    firefightDays: 3,
  },
  {
    name: 'Email deliverability uplift',
    team: 'Notifications',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 7,
    lateWeeks: 2,
    firefightDays: 6,
  },
  {
    name: 'Anti-fraud rules engine v4',
    team: 'Anti-Fraud',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+5 wk)',
    headcount: 6,
    reworkDays: 19,
    lateWeeks: 5,
    firefightDays: 9,
  },
  {
    name: 'Tax rate engine for EU',
    team: 'Monetization',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 12,
    lateWeeks: 3,
    firefightDays: 5,
  },
  {
    name: 'Web push for Safari',
    team: 'Web Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+4 wk)',
    headcount: 3,
    reworkDays: 10,
    lateWeeks: 4,
    firefightDays: 5,
  },
  {
    name: 'On-call paging rewrite',
    team: 'Observability',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 8,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'Read replica failover automation',
    team: 'Storage',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 8,
  },
  {
    name: 'API key rotation tooling',
    team: 'Identity & Access',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+1 wk)',
    headcount: 2,
    reworkDays: 5,
    lateWeeks: 1,
    firefightDays: 3,
  },
  {
    name: 'Search type-ahead latency fix',
    team: 'Search',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 7,
    lateWeeks: 2,
    firefightDays: 5,
  },
  {
    name: 'Embedded analytics for sellers',
    team: 'Web Platform',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+4 wk)',
    headcount: 5,
    reworkDays: 15,
    lateWeeks: 4,
    firefightDays: 6,
  },
  {
    name: 'Subscription dunning v2',
    team: 'Monetization',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 12,
    lateWeeks: 3,
    firefightDays: 7,
  },
  {
    name: 'Privacy policy auto-redactor',
    team: 'Trust & Safety',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+5 wk)',
    headcount: 4,
    reworkDays: 14,
    lateWeeks: 5,
    firefightDays: 4,
  },
  {
    name: 'Cross-region failover drill tooling',
    team: 'Core Infrastructure',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 5,
    reworkDays: 12,
    lateWeeks: 3,
    firefightDays: 6,
  },
  {
    name: 'Mobile SDK telemetry',
    team: 'Mobile Core',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 7,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'Order status webhooks',
    team: 'Partner Integrations',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+1 wk)',
    headcount: 3,
    reworkDays: 5,
    lateWeeks: 1,
    firefightDays: 3,
  },
  {
    name: 'Refunds latency improvements',
    team: 'Payments Platform',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 4,
    reworkDays: 9,
    lateWeeks: 2,
    firefightDays: 5,
  },
  {
    name: 'Search facet personalization',
    team: 'Search',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+4 wk)',
    headcount: 4,
    reworkDays: 13,
    lateWeeks: 4,
    firefightDays: 6,
  },
  {
    name: 'Bulk customer messaging',
    team: 'Notifications',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 5,
  },
  {
    name: 'Webhook signing rotation',
    team: 'API Gateway',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 6,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'Admin audit log v2',
    team: 'Trust & Safety',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 8,
    lateWeeks: 2,
    firefightDays: 3,
  },
  {
    name: 'iOS deep-link router',
    team: 'Mobile Foundations',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 3,
    reworkDays: 10,
    lateWeeks: 3,
    firefightDays: 4,
  },
  {
    name: 'Realtime presence service',
    team: 'Web Platform',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+4 wk)',
    headcount: 4,
    reworkDays: 14,
    lateWeeks: 4,
    firefightDays: 8,
  },
  {
    name: 'Inventory consistency checker',
    team: 'Storage',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 4,
    reworkDays: 9,
    lateWeeks: 2,
    firefightDays: 5,
  },
  {
    name: 'Self-serve billing portal',
    team: 'Monetization',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+6 wk)',
    headcount: 5,
    reworkDays: 18,
    lateWeeks: 6,
    firefightDays: 7,
  },
  {
    name: 'Onboarding email sequence v3',
    team: 'Growth Engineering',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 7,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'Saved-card vault migration',
    team: 'Payments Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+4 wk)',
    headcount: 5,
    reworkDays: 15,
    lateWeeks: 4,
    firefightDays: 10,
  },
  {
    name: 'GraphQL field deprecation tooling',
    team: 'API Gateway',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 7,
    lateWeeks: 2,
    firefightDays: 3,
  },
  {
    name: 'In-app chat support v2',
    team: 'Customer Success Eng',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 6,
  },
  {
    name: 'KYC document re-verification flow',
    team: 'Trust & Safety',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+5 wk)',
    headcount: 4,
    reworkDays: 14,
    lateWeeks: 5,
    firefightDays: 5,
  },
  {
    name: 'Mobile crash triage automation',
    team: 'Mobile Core',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 6,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'Region-aware caching',
    team: 'Web Platform',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 7,
  },
  {
    name: 'Reporting export API',
    team: 'Data Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 10,
    lateWeeks: 3,
    firefightDays: 5,
  },
  {
    name: 'Customer-segment recompute pipeline',
    team: 'Data Platform',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+4 wk)',
    headcount: 4,
    reworkDays: 13,
    lateWeeks: 4,
    firefightDays: 5,
  },
  {
    name: 'PCI scope reduction',
    team: 'Payments Platform',
    plannedShip: 'Q1 2025',
    actualShip: 'Q3 2025 (+8 wk)',
    headcount: 6,
    reworkDays: 25,
    lateWeeks: 8,
    firefightDays: 6,
  },
  {
    name: 'Webhook delivery dashboards',
    team: 'Observability',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+1 wk)',
    headcount: 3,
    reworkDays: 5,
    lateWeeks: 1,
    firefightDays: 3,
  },
  {
    name: 'EU data-residency rollout',
    team: 'Storage',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+9 wk)',
    headcount: 6,
    reworkDays: 22,
    lateWeeks: 9,
    firefightDays: 5,
  },
  {
    name: 'Mobile attribution SDK swap',
    team: 'Mobile Foundations',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+4 wk)',
    headcount: 3,
    reworkDays: 11,
    lateWeeks: 4,
    firefightDays: 4,
  },
  {
    name: 'Customer health-score model',
    team: 'ML Infra',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 12,
    lateWeeks: 3,
    firefightDays: 5,
  },
  {
    name: 'Subscription proration fix',
    team: 'Monetization',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 8,
    lateWeeks: 2,
    firefightDays: 6,
  },
  {
    name: 'Search query autocompletion v2',
    team: 'Search',
    plannedShip: 'Q3 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 4,
    reworkDays: 9,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'Inbox unification across products',
    team: 'Web Platform',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+5 wk)',
    headcount: 5,
    reworkDays: 17,
    lateWeeks: 5,
    firefightDays: 7,
  },
  {
    name: 'Service-mesh sidecar upgrade',
    team: 'Core Infrastructure',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+3 wk)',
    headcount: 5,
    reworkDays: 13,
    lateWeeks: 3,
    firefightDays: 8,
  },
  {
    name: 'Multi-currency invoicing',
    team: 'Monetization',
    plannedShip: 'Q3 2025',
    actualShip: 'Q4 2025 (+5 wk)',
    headcount: 4,
    reworkDays: 16,
    lateWeeks: 5,
    firefightDays: 5,
  },
  {
    name: 'SSO connector for Okta',
    team: 'Identity & Access',
    plannedShip: 'Q2 2025',
    actualShip: 'Q3 2025 (+2 wk)',
    headcount: 3,
    reworkDays: 8,
    lateWeeks: 2,
    firefightDays: 4,
  },
  {
    name: 'Anti-spam classifier retraining',
    team: 'Trust & Safety',
    plannedShip: 'Q1 2025',
    actualShip: 'Q2 2025 (+3 wk)',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 3,
    firefightDays: 5,
  },
];

const missedTemplates: StandardTemplate[] = [
  {
    name: 'Datacenter vendor migration',
    team: 'Core Infrastructure',
    plannedShip: 'Q2 2025',
    actualShip: 'Cancelled',
    headcount: 8,
    reworkDays: 30,
    lateWeeks: 12,
    firefightDays: 0,
  },
  {
    name: 'EU AI Act compliance retrofit',
    team: 'Trust & Safety',
    plannedShip: 'Q3 2025',
    actualShip: 'Q1 2026 (+14 wk)',
    headcount: 5,
    reworkDays: 18,
    lateWeeks: 14,
    firefightDays: 0,
  },
  {
    name: 'Marketplace payouts overhaul',
    team: 'Payments Platform',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+10 wk)',
    headcount: 6,
    reworkDays: 0,
    lateWeeks: 10,
    firefightDays: 18,
  },
  {
    name: 'Customer onboarding revamp',
    team: 'Growth Engineering',
    plannedShip: 'Q3 2025',
    actualShip: 'Cancelled',
    headcount: 4,
    reworkDays: 14,
    lateWeeks: 8,
    firefightDays: 0,
  },
  {
    name: 'Real-time fraud scoring',
    team: 'Anti-Fraud',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+11 wk)',
    headcount: 7,
    reworkDays: 22,
    lateWeeks: 11,
    firefightDays: 9,
  },
  {
    name: 'Internal admin portal rebuild',
    team: 'Web Platform',
    plannedShip: 'Q1 2025',
    actualShip: 'Cancelled',
    headcount: 5,
    reworkDays: 16,
    lateWeeks: 9,
    firefightDays: 0,
  },
  {
    name: 'New regulatory reporting pipeline',
    team: 'Data Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Q1 2026 (+13 wk)',
    headcount: 4,
    reworkDays: 12,
    lateWeeks: 13,
    firefightDays: 0,
  },
  {
    name: 'iPad-native experience',
    team: 'Mobile Foundations',
    plannedShip: 'Q2 2025',
    actualShip: 'Cancelled',
    headcount: 5,
    reworkDays: 18,
    lateWeeks: 10,
    firefightDays: 0,
  },
  {
    name: 'GDPR data-deletion automation',
    team: 'Trust & Safety',
    plannedShip: 'Q1 2025',
    actualShip: 'Q3 2025 (+10 wk)',
    headcount: 4,
    reworkDays: 0,
    lateWeeks: 10,
    firefightDays: 7,
  },
  {
    name: 'Hardware token integration',
    team: 'Identity & Access',
    plannedShip: 'Q3 2025',
    actualShip: 'Cancelled',
    headcount: 3,
    reworkDays: 8,
    lateWeeks: 7,
    firefightDays: 0,
  },
  {
    name: 'On-prem connector for enterprise',
    team: 'Partner Integrations',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+9 wk)',
    headcount: 5,
    reworkDays: 12,
    lateWeeks: 9,
    firefightDays: 6,
  },
  {
    name: 'Real-time collaboration cursors',
    team: 'Web Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Cancelled',
    headcount: 4,
    reworkDays: 14,
    lateWeeks: 8,
    firefightDays: 0,
  },
  {
    name: 'Compliance attestation portal',
    team: 'Trust & Safety',
    plannedShip: 'Q2 2025',
    actualShip: 'Q1 2026 (+15 wk)',
    headcount: 3,
    reworkDays: 9,
    lateWeeks: 15,
    firefightDays: 0,
  },
  {
    name: 'BI tool migration to Looker',
    team: 'Data Platform',
    plannedShip: 'Q1 2025',
    actualShip: 'Q3 2025 (+11 wk)',
    headcount: 4,
    reworkDays: 0,
    lateWeeks: 11,
    firefightDays: 5,
  },
  {
    name: 'Procurement systems integration',
    team: 'Sales Engineering',
    plannedShip: 'Q3 2025',
    actualShip: 'Cancelled',
    headcount: 3,
    reworkDays: 10,
    lateWeeks: 6,
    firefightDays: 0,
  },
  {
    name: 'Cross-product unified profile',
    team: 'Identity & Access',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+12 wk)',
    headcount: 6,
    reworkDays: 18,
    lateWeeks: 12,
    firefightDays: 4,
  },
  {
    name: 'Custom contract pricing engine',
    team: 'Monetization',
    plannedShip: 'Q1 2025',
    actualShip: 'Cancelled',
    headcount: 4,
    reworkDays: 11,
    lateWeeks: 9,
    firefightDays: 0,
  },
  {
    name: 'Voice-based support agent',
    team: 'Customer Success Eng',
    plannedShip: 'Q3 2025',
    actualShip: 'Cancelled',
    headcount: 5,
    reworkDays: 16,
    lateWeeks: 10,
    firefightDays: 0,
  },
  {
    name: 'New observability backend trial',
    team: 'Observability',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+10 wk)',
    headcount: 4,
    reworkDays: 0,
    lateWeeks: 10,
    firefightDays: 6,
  },
  {
    name: 'GovCloud deployment pipeline',
    team: 'Core Infrastructure',
    plannedShip: 'Q3 2025',
    actualShip: 'Cancelled',
    headcount: 5,
    reworkDays: 14,
    lateWeeks: 9,
    firefightDays: 0,
  },
  {
    name: 'External data-broker partnership',
    team: 'Partner Integrations',
    plannedShip: 'Q1 2025',
    actualShip: 'Q3 2025 (+11 wk)',
    headcount: 3,
    reworkDays: 0,
    lateWeeks: 11,
    firefightDays: 4,
  },
  {
    name: 'Enterprise SCIM provisioning',
    team: 'Identity & Access',
    plannedShip: 'Q2 2025',
    actualShip: 'Q4 2025 (+10 wk)',
    headcount: 4,
    reworkDays: 12,
    lateWeeks: 10,
    firefightDays: 0,
  },
  {
    name: 'Carbon footprint reporting',
    team: 'Data Platform',
    plannedShip: 'Q3 2025',
    actualShip: 'Cancelled',
    headcount: 3,
    reworkDays: 7,
    lateWeeks: 6,
    firefightDays: 0,
  },
];

const missedRationales: Record<string, string> = {
  'Datacenter vendor migration':
    'Cancelled when the vendor failed an internal security review - no ticket or commit pattern Intera looks at could have predicted this.',
  'EU AI Act compliance retrofit':
    'Scope kept expanding as regulatory guidance was published - the slip is in legal interpretation, not in engineering signals.',
  'Marketplace payouts overhaul':
    'Bank partner missed two SLA deadlines on the integration sandbox - the delay lives in external commitments, not in our tools.',
  'Customer onboarding revamp':
    'Cancelled following a strategic pivot away from self-serve - no engineering-side anomaly preceded the decision.',
  'Real-time fraud scoring':
    'Tech lead departed mid-project; the slip is attributable to key-person attrition rather than detectable rework.',
  'Internal admin portal rebuild':
    'De-prioritized after a leadership change re-ranked the roadmap - the original work continued normally until the kill decision.',
  'New regulatory reporting pipeline':
    'Local regulator changed the reporting schema twice - Intera does not track regulatory artifacts as inputs.',
  'iPad-native experience':
    'Cancelled after an Apple platform change made the planned approach obsolete - no in-repo signature predicts this.',
  'GDPR data-deletion automation':
    'Awaiting legal sign-off on a clause for 7 months - the delay lives in legal review, not in engineering signals.',
  'Hardware token integration':
    'Cancelled when the partner vendor was acquired and the SDK was discontinued.',
  'On-prem connector for enterprise':
    'Customer pushed deployment to their next budget cycle - the late ship reflects customer scheduling, not engineering slippage.',
  'Real-time collaboration cursors':
    'Cancelled following a product-strategy pivot - engineering execution was on track until the decision.',
  'Compliance attestation portal':
    'Audit firm changed methodology halfway through - the slip is in external requirements, not in our tools.',
  'BI tool migration to Looker':
    'Vendor restructuring froze the migration timeline for two quarters.',
  'Procurement systems integration':
    "Cancelled when the customer's procurement team was reorganized.",
  'Cross-product unified profile':
    'Required an executive decision on data-sharing policy that took two quarters to resolve.',
  'Custom contract pricing engine':
    'Cancelled when the lighthouse customer chose a competitor - no engineering-side anomaly.',
  'Voice-based support agent':
    'Cancelled after vendor demos failed to meet quality bar - no in-repo signature.',
  'New observability backend trial':
    'Vendor evaluation extended after a SOC 2 finding - delay is in compliance, not in code.',
  'GovCloud deployment pipeline':
    'Cancelled when the relevant FedRAMP authorization milestone was missed by the partner agency.',
  'External data-broker partnership':
    'Partner renegotiated data-sharing terms - the delay lives in legal, not engineering.',
  'Enterprise SCIM provisioning':
    'Two enterprise customers requested incompatible variants of the spec - the slip is in product scoping, not in detectable rework.',
  'Carbon footprint reporting':
    'Cancelled after the proposed framework was deprecated by the standards body.',
};

interface SourceArtifactConfig {
  source: RoiSignalSource;
  refTemplate: (params: {
    index: number;
    teamSlug: string;
    teamPrefix: string;
  }) => string;
  description: string;
}

const SOURCE_ARTIFACTS: SourceArtifactConfig[] = [
  {
    source: 'jira',
    refTemplate: ({ teamPrefix, index }) =>
      `${teamPrefix}-${1000 + ((index * 37) % 8000)}`,
    description:
      'Same ticket reopened twice within ten days - Intera classifies as a rework signature.',
  },
  {
    source: 'github',
    refTemplate: ({ index }) => `PR #${10000 + ((index * 91) % 12000)}`,
    description: 'Revert commit on the central PR within a fortnight of merge.',
  },
  {
    source: 'confluence',
    refTemplate: ({ teamSlug }) => `confluence/${teamSlug}-prd`,
    description:
      'PRD edited four times after the build kickoff - scope drift detected.',
  },
  {
    source: 'sharepoint',
    refTemplate: ({ teamSlug }) => `sp:${teamSlug}/PRD-v${2 + ((1 * 1) % 4)}`,
    description:
      'SharePoint spec versioned three times during the implementation window.',
  },
  {
    source: 'salesforce',
    refTemplate: ({ index }) => `SF-CASE-${4000 + ((index * 31) % 1200)}`,
    description:
      'Customer-commit case escalated when ship-date slipped past the contracted milestone.',
  },
];

function buildSignalsFor(
  index: number,
  template: StandardTemplate
): RoiSignal[] {
  const teamSlug = template.team.toLowerCase().replace(/[^a-z]+/g, '-');
  const teamPrefix = (template.team.split(' ')[0] ?? 'TEAM').toUpperCase();
  const pickCount = 2 + (index % 3);
  const signals: RoiSignal[] = [];
  for (let signalIndex = 0; signalIndex < pickCount; signalIndex += 1) {
    const artifact =
      SOURCE_ARTIFACTS[(index + signalIndex * 3) % SOURCE_ARTIFACTS.length] ??
      SOURCE_ARTIFACTS[0]!;
    signals.push({
      source: artifact.source,
      ref: artifact.refTemplate({ index, teamSlug, teamPrefix }),
      description: artifact.description,
    });
  }
  return signals;
}

function caughtRationaleFor(template: StandardTemplate): string {
  if (
    template.reworkDays >= template.lateWeeks * 4 &&
    template.reworkDays > 10
  ) {
    return "Repeated reopens and revert commits during the build window - Intera's rework signature would have surfaced this before the planned ship.";
  }
  if (template.firefightDays >= 8) {
    return "A burst of post-launch incidents and hotfix PRs in the first 30 days - Intera's firefight detector would have linked them back to the project.";
  }
  if (template.lateWeeks >= 5) {
    return 'PR review cycles and ticket reopens in the final third of the build - Intera would have flagged scope drift before the deadline slipped further.';
  }
  return "Ticket reopens and post-launch hotfixes both fired during this project - Intera's rework and firefight detectors would have surfaced it.";
}

function buildStandardProject(
  template: StandardTemplate,
  verdict: RoiVerdict,
  index: number
): RoiProject {
  const rationale =
    verdict === 'caught'
      ? caughtRationaleFor(template)
      : missedRationales[template.name] ??
        'Failure mode falls outside the ticket / commit / message-graph signals Intera currently looks at.';
  return {
    id: template.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, ''),
    name: template.name,
    team: template.team,
    tier: 'standard',
    verdict,
    verdictRationale: rationale,
    failureModes: failureModesFor(
      template.reworkDays,
      template.lateWeeks,
      template.firefightDays
    ),
    reworkEngineerDays: template.reworkDays,
    weeksSlipped: template.lateWeeks,
    firefightEngineerDays: template.firefightDays,
    plannedShip: template.plannedShip,
    actualShip: template.actualShip,
    headcount: template.headcount,
    signals: verdict === 'caught' ? buildSignalsFor(index, template) : [],
  };
}

const standardCaught: RoiProject[] = caughtTemplates.map((template, index) =>
  buildStandardProject(template, 'caught', index)
);

const missedProjects: RoiProject[] = missedTemplates.map((template, index) =>
  buildStandardProject(template, 'missed', index)
);

const allProjects: RoiProject[] = [
  ...heroProjects,
  ...standardCaught,
  ...missedProjects,
];

const caughtProjects = allProjects.filter(
  (project) => project.verdict === 'caught'
);

const heroTotal = REWORK_RECOVERY_USD;

const caughtLateProjects = caughtProjects.filter(
  (project) => project.weeksSlipped > 0
);
const totalWeeksSlipped = caughtLateProjects.reduce(
  (sum, project) => sum + project.weeksSlipped,
  0
);
const worstSlipProject = caughtLateProjects.reduce<RoiProject | null>(
  (worst, project) =>
    worst === null || project.weeksSlipped > worst.weeksSlipped
      ? project
      : worst,
  null
);

const caughtFirefightProjects = caughtProjects.filter(
  (project) => project.firefightEngineerDays > 0
);
const totalFirefightDays = caughtFirefightProjects.reduce(
  (sum, project) => sum + project.firefightEngineerDays,
  0
);
const p1IncidentCount = Math.max(1, Math.round(totalFirefightDays * 0.18));
const hotfixPrCount = Math.max(1, Math.round(totalFirefightDays * 0.42));
const escalationMeetingsCount = Math.max(
  1,
  Math.round(totalFirefightDays * 0.24)
);

const buckets: {
  rework: RoiReworkBucket;
  late: RoiLateMetricBucket;
  firefight: RoiFirefightMetricBucket;
} = {
  rework: {
    title: 'Rework during development',
    lifecycleLabel: 'Before "done"',
    formula: {
      hoursPerPersonPerMonth: RECOVERABLE_REWORK_HOURS_PER_PERSON_PER_MONTH,
      engineeringHeadcount: ENGINEERING_HEADCOUNT,
      ratePerEngineerHourUsd: RATE_PER_ENGINEER_HOUR_USD,
      monthsPerYear: MONTHS_PER_YEAR,
      totalUsd: REWORK_RECOVERY_USD,
    },
    exampleQuote:
      'Three reopen events on the same Jira ticket inside two weeks - Intera would have caught this on day 14.',
    exampleProject: 'Payments idempotency hardening',
  },
  late: {
    title: 'Late shipping',
    lifecycleLabel: 'Past the deadline',
    projectsLate: caughtLateProjects.length,
    totalWeeksSlipped,
    worstSlipWeeks: worstSlipProject?.weeksSlipped ?? 0,
    worstSlipProject: worstSlipProject?.name ?? '-',
    exampleQuote:
      'Six engineers spent eight extra weeks finishing the Postgres sharding migration past the planned Q1 ship.',
  },
  firefight: {
    title: 'Post-launch firefighting',
    lifecycleLabel: 'After ship',
    projectsWithFirefight: caughtFirefightProjects.length,
    p1IncidentCount,
    hotfixPrCount,
    escalationMeetingsCount,
    exampleQuote:
      'A P1 duplicate-charge incident nine days after launch consumed three weeks of incident response.',
    exampleProject: 'Payments idempotency hardening',
  },
};

const totalProjectCount = allProjects.length;
const caughtProjectCount = caughtProjects.length;
const finalRecallPct = Math.round(
  (caughtProjectCount / totalProjectCount) * 100
);

interface IntegrationConfig {
  source: RoiSignalSource;
  label: string;
  signalDescription: string;
  share: number;
}

const integrationConfigs: IntegrationConfig[] = [
  {
    source: 'jira',
    label: 'Jira',
    signalDescription:
      'Ticket reopens, late scope edits, repeated assignee bounces - the rework signature.',
    share: 0.35,
  },
  {
    source: 'sharepoint',
    label: 'SharePoint',
    signalDescription:
      'Repeated PRD revisions, late stakeholder edits, branch versions of the same spec.',
    share: 0.12,
  },
  {
    source: 'confluence',
    label: 'Confluence',
    signalDescription:
      'PRD edits mid-build, runbook churn, design-doc revisions that signal scope drift.',
    share: 0.1,
  },
  {
    source: 'salesforce',
    label: 'Salesforce',
    signalDescription:
      'Customer-commit dates slipping, escalation cases tied back to the project epic.',
    share: 0.08,
  },
  {
    source: 'github',
    label: 'GitHub',
    signalDescription:
      'Revert commits, abnormal review-round counts, force-push storms on the central PR.',
    share: 0.25,
  },
];

const primaryCatcherCounts: Record<RoiSignalSource, number> = {
  jira: 0,
  linear: 0,
  monday: 0,
  asana: 0,
  github: 0,
  slack: 0,
  teams: 0,
  confluence: 0,
  notion: 0,
  sharepoint: 0,
  gdrive: 0,
  pagerduty: 0,
  salesforce: 0,
  hubspot: 0,
};
for (const caughtProject of caughtProjects) {
  const primarySource = caughtProject.signals[0]?.source;
  if (primarySource) {
    primaryCatcherCounts[primarySource] += 1;
  }
}

const integrationSteps: RoiIntegrationStep[] = (() => {
  let cumulative = 0;
  return integrationConfigs.map((config) => {
    const projectsUnlocked = primaryCatcherCounts[config.source];
    const recallDeltaPct = Math.round(
      (projectsUnlocked / totalProjectCount) * 100
    );
    cumulative += recallDeltaPct;
    return {
      source: config.source,
      label: config.label,
      signalDescription: config.signalDescription,
      recallDeltaPct,
      cumulativeRecallPct: cumulative,
      projectsUnlocked,
    };
  });
})();

const integrationCoverage: RoiIntegrationCoverage = {
  baselinePct: 0,
  finalPct: finalRecallPct,
  steps: integrationSteps,
};

const MONTH_LABELS_TRAILING_12 = [
  "Jun '25",
  "Jul '25",
  "Aug '25",
  "Sep '25",
  "Oct '25",
  "Nov '25",
  "Dec '25",
  "Jan '26",
  "Feb '26",
  "Mar '26",
  "Apr '26",
  "May '26",
];

function buildMonthlyCumulative(total: number): RoiCumulativePoint[] {
  const monthlyWeights = [
    3.6, 4.2, 5.1, 5.8, 6.4, 7.1, 7.9, 8.6, 9.5, 10.4, 11.2, 12.2,
  ];
  const weightSum = monthlyWeights.reduce((sum, weight) => sum + weight, 0);
  const monthlyAmounts = monthlyWeights.map((weight) =>
    Math.round((weight / weightSum) * total)
  );
  const drift = total - monthlyAmounts.reduce((sum, value) => sum + value, 0);
  monthlyAmounts[monthlyAmounts.length - 1] =
    (monthlyAmounts[monthlyAmounts.length - 1] ?? 0) + drift;
  let running = 0;
  return MONTH_LABELS_TRAILING_12.map((monthLabel, index) => {
    const monthlyUsd = monthlyAmounts[index] ?? 0;
    running += monthlyUsd;
    return { monthLabel, monthlyUsd, cumulativeUsd: running };
  });
}

const monthlyCumulative = buildMonthlyCumulative(heroTotal);

export const ROI_DEMO_DATASET: RoiDataset = {
  heroTotalUsd: heroTotal,
  totalProjects: totalProjectCount,
  caughtProjects: caughtProjectCount,
  missedProjects: totalProjectCount - caughtProjectCount,
  assumptions: {
    engineeringHeadcount: ENGINEERING_HEADCOUNT,
    blendedSalaryUsd: BLENDED_SALARY_USD,
    workingDaysPerYear: WORKING_DAYS_PER_YEAR,
    ratePerEngineerDayUsd: RATE_PER_ENGINEER_DAY_USD,
    ratePerEngineerHourUsd: RATE_PER_ENGINEER_HOUR_USD,
    timeWindowLabel: 'Trailing 12 months',
  },
  buckets,
  monthlyCumulative,
  integrationCoverage,
  projects: allProjects,
};

export function getRoiProjectById(projectId: string): RoiProject | undefined {
  return ROI_DEMO_DATASET.projects.find((project) => project.id === projectId);
}
